import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { naytiveAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import './NaytiveIncorporation.css';
// Standard admin chrome — dashboard-layout / data-table / modal-form
// / form-row + form-group / stat-card / btn-action etc. all live here.
import './VisaManagement.css';

/**
 * Admin console for the Naytive incorporation engine.
 *
 * Three tabs:
 *   1. Jurisdictions — list + edit the 15 jurisdiction profiles
 *      (costs, weights, industry matrix, gotchas, tax scores).
 *   2. Scoring config — global tunable weights (vc_base, us_treaty, etc.).
 *   3. Recommendations log — every call the engine has scored, with
 *      override + pick metadata.
 *
 * Every save on the first two tabs triggers a backend-side cache reload
 * in user/backend so the in-memory engine picks up the change without
 * a deploy.
 */
const TABS = [
  { id: 'jurisdictions', label: 'Jurisdictions' },
  { id: 'config', label: 'Scoring Config' },
  { id: 'log', label: 'Recommendations Log' },
];

const NaytiveIncorporation = () => {
  const [tab, setTab] = useState('jurisdictions');
  const [toast, setToast] = useState(null);
  const flash = useCallback((message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">INCORPORATION ENGINE</h1>
            <p className="page-subtitle">
              JURISDICTIONS · SCORING · RECOMMENDATIONS · ALL EDITS HOT-RELOAD
            </p>
          </div>
        </div>

        <div className="dashboard-content">
          {/* Tab strip — same visual as VisaManagement's tabs row. */}
          <div className="dashboard-section" style={{ padding: '12px 16px', marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 4 }}>
              {TABS.map((t) => (
                <button
                  key={t.id}
                  className={`tab-btn ${tab === t.id ? 'active' : ''}`}
                  onClick={() => setTab(t.id)}
                >
                  {t.label.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {tab === 'jurisdictions' && <JurisdictionsTab flash={flash} />}
          {tab === 'config' && <ConfigTab flash={flash} />}
          {tab === 'log' && <LogTab />}
        </div>

        {toast && (
          <div className={`naytive-toast ${toast.type === 'error' ? 'toast-error' : 'toast-success'}`}>
            {toast.message}
          </div>
        )}
      </main>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// JURISDICTIONS TAB
// ─────────────────────────────────────────────────────────────────────────────

const PricingModal = ({ row, onClose, flash }) => {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [newDraft, setNewDraft] = useState({ label: '', amount: '', currency: 'USD' });

  const fetchComponents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await naytiveAPI.listPricingComponents({
        jurisdictionSlug: row.slug,
      });
      setComponents(res.data?.items || []);
    } catch (err) {
      console.error(err);
      flash('Failed to load pricing', 'error');
    } finally {
      setLoading(false);
    }
  }, [row.slug, flash]);

  useEffect(() => { fetchComponents(); }, [fetchComponents]);

  const total = useMemo(
    () => components.reduce((s, r) => s + Number(r.amount_cents), 0),
    [components],
  );

  const addRow = async () => {
    if (!newDraft.label.trim() || !Number.isFinite(Number(newDraft.amount))) {
      flash('Label and amount required', 'error');
      return;
    }
    setSavingId('new');
    try {
      await naytiveAPI.createPricingComponent({
        jurisdictionSlug: row.slug,
        label: newDraft.label.trim(),
        amountCents: Math.round(Number(newDraft.amount) * 100),
        currency: newDraft.currency,
        displayOrder: components.length + 1,
      });
      setNewDraft({ label: '', amount: '', currency: 'USD' });
      await fetchComponents();
      flash('Added');
    } catch (err) {
      flash(err.response?.data?.message || 'Add failed', 'error');
    } finally {
      setSavingId(null);
    }
  };

  const updateRow = async (id, patch) => {
    setSavingId(id);
    try {
      await naytiveAPI.updatePricingComponent(id, patch);
      await fetchComponents();
      flash('Saved');
    } catch (err) {
      flash(err.response?.data?.message || 'Save failed', 'error');
    } finally {
      setSavingId(null);
    }
  };

  const deleteRow = async (id) => {
    if (!window.confirm('Delete this pricing component?')) return;
    setSavingId(id);
    try {
      await naytiveAPI.deletePricingComponent(id);
      await fetchComponents();
      flash('Deleted');
    } catch (err) {
      flash(err.response?.data?.message || 'Delete failed', 'error');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFFFFF',
          borderRadius: 16,
          width: 'min(720px, 100%)',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(15,23,42,0.25)',
        }}
      >
        <div style={{ padding: '20px 24px 14px', borderBottom: '1px solid #E5E7EB' }}>
          <div style={{ fontSize: 11, color: '#9CA3AF', letterSpacing: 0.5, fontWeight: 600 }}>
            TENTATIVE PRICING · {row.country_code}
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>
            {row.name} <span style={{ color: '#9CA3AF', fontSize: 13, fontWeight: 400 }}>({row.slug})</span>
          </div>
          <div style={{ marginTop: 6, fontSize: 13, color: '#6B7280' }}>
            Copied into every new proposal for this jurisdiction. Edits here
            only affect future bookings.
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {loading ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: '#9CA3AF' }}>Loading…</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #E5E7EB', textAlign: 'left' }}>
                  <th style={{ padding: '8px 8px', width: 60, color: '#6B7280', fontWeight: 600, fontSize: 11, letterSpacing: 0.4 }}>ORDER</th>
                  <th style={{ padding: '8px 8px', color: '#6B7280', fontWeight: 600, fontSize: 11, letterSpacing: 0.4 }}>LABEL</th>
                  <th style={{ padding: '8px 8px', width: 130, color: '#6B7280', fontWeight: 600, fontSize: 11, letterSpacing: 0.4 }}>AMOUNT</th>
                  <th style={{ padding: '8px 8px', width: 80, color: '#6B7280', fontWeight: 600, fontSize: 11, letterSpacing: 0.4 }}>CCY</th>
                  <th style={{ padding: '8px 8px', width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {components.map((c) => (
                  <ComponentRow
                    key={c.id}
                    row={c}
                    saving={savingId === c.id}
                    onSave={(patch) => updateRow(c.id, patch)}
                    onDelete={() => deleteRow(c.id)}
                  />
                ))}
                <tr style={{ background: '#F9FAFB' }}>
                  <td style={{ padding: '10px 8px', color: '#9CA3AF', fontSize: 12 }}>
                    {components.length + 1}
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    <input
                      type="text"
                      value={newDraft.label}
                      onChange={(e) => setNewDraft((p) => ({ ...p, label: e.target.value }))}
                      placeholder="e.g. Local law firm fees"
                      style={cellInput}
                    />
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    <input
                      type="number"
                      step="0.01"
                      value={newDraft.amount}
                      onChange={(e) => setNewDraft((p) => ({ ...p, amount: e.target.value }))}
                      placeholder="2000.00"
                      style={cellInput}
                    />
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    <select
                      value={newDraft.currency}
                      onChange={(e) => setNewDraft((p) => ({ ...p, currency: e.target.value }))}
                      style={cellInput}
                    >
                      <option>USD</option>
                      <option>EUR</option>
                      <option>GBP</option>
                      <option>INR</option>
                      <option>MXN</option>
                      <option>AED</option>
                      <option>SGD</option>
                    </select>
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    <button
                      className="naytive-incorp-btn"
                      onClick={addRow}
                      disabled={savingId === 'new'}
                    >
                      {savingId === 'new' ? 'Adding…' : 'Add'}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>

        <div
          style={{
            padding: '14px 24px',
            borderTop: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: '#FAFAFA',
          }}
        >
          <div>
            <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, letterSpacing: 0.4 }}>
              TENTATIVE TOTAL
            </span>
            <span style={{ marginLeft: 10, fontSize: 18, fontWeight: 700 }}>
              ${(total / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}
            </span>
          </div>
          <button className="naytive-incorp-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────── Required Documents Modal ──────────────────────── */

const RequiredDocsModal = ({ row, onClose, flash }) => {
  const [docs, setDocs] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [newDraft, setNewDraft] = useState({
    document_type_id: '',
    custom_label: '',
    subtitle: '',
    optional: false,
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [docsRes, typesRes] = await Promise.all([
        naytiveAPI.listRequiredDocs({ jurisdictionSlug: row.slug }),
        naytiveAPI.listDocumentTypes(),
      ]);
      setDocs(docsRes.data?.items || []);
      setDocTypes(typesRes.data?.items || []);
    } catch (err) {
      console.error(err);
      flash('Failed to load required documents', 'error');
    } finally {
      setLoading(false);
    }
  }, [row.slug, flash]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const usedTypeIds = useMemo(
    () => new Set(docs.map((d) => d.document_type_id)),
    [docs],
  );
  const availableTypes = useMemo(
    () => docTypes.filter((t) => !usedTypeIds.has(t.id)),
    [docTypes, usedTypeIds],
  );

  const addRow = async () => {
    if (!newDraft.document_type_id) {
      flash('Pick a document type', 'error');
      return;
    }
    setSavingId('new');
    try {
      await naytiveAPI.createRequiredDoc({
        jurisdiction_slug: row.slug,
        document_type_id: Number(newDraft.document_type_id),
        custom_label: newDraft.custom_label.trim() || null,
        subtitle: newDraft.subtitle.trim() || null,
        optional: newDraft.optional,
        display_order: docs.length + 1,
      });
      setNewDraft({
        document_type_id: '', custom_label: '', subtitle: '', optional: false,
      });
      await fetchAll();
      flash('Added');
    } catch (err) {
      flash(err.response?.data?.message || 'Add failed', 'error');
    } finally {
      setSavingId(null);
    }
  };

  const updateRow = async (id, patch) => {
    setSavingId(id);
    try {
      await naytiveAPI.updateRequiredDoc(id, patch);
      await fetchAll();
      flash('Saved');
    } catch (err) {
      flash(err.response?.data?.message || 'Save failed', 'error');
    } finally {
      setSavingId(null);
    }
  };

  const deleteRow = async (id) => {
    if (!window.confirm('Remove this document from the jurisdiction defaults?')) return;
    setSavingId(id);
    try {
      await naytiveAPI.deleteRequiredDoc(id);
      await fetchAll();
      flash('Removed');
    } catch (err) {
      flash(err.response?.data?.message || 'Delete failed', 'error');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        zIndex: 2000, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFFFFF', borderRadius: 16,
          width: 'min(820px, 100%)', maxHeight: '85vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(15,23,42,0.25)',
        }}
      >
        <div style={{ padding: '20px 24px 14px', borderBottom: '1px solid #E5E7EB' }}>
          <div style={{ fontSize: 11, color: '#9CA3AF', letterSpacing: 0.5, fontWeight: 600 }}>
            REQUIRED DOCUMENTS · {row.country_code}
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>
            {row.name}{' '}
            <span style={{ color: '#9CA3AF', fontSize: 13, fontWeight: 400 }}>
              ({row.slug})
            </span>
          </div>
          <div style={{ marginTop: 6, fontSize: 13, color: '#6B7280' }}>
            Defaults copied into every new proposal&apos;s requirements list.
            Admin can override per-case from the Proposals page.
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {loading ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: '#9CA3AF' }}>
              Loading…
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #E5E7EB', textAlign: 'left' }}>
                  <th style={{ padding: '8px', width: 50, color: '#6B7280', fontWeight: 600, fontSize: 11, letterSpacing: 0.4 }}>#</th>
                  <th style={{ padding: '8px', color: '#6B7280', fontWeight: 600, fontSize: 11, letterSpacing: 0.4 }}>DOC TYPE</th>
                  <th style={{ padding: '8px', color: '#6B7280', fontWeight: 600, fontSize: 11, letterSpacing: 0.4 }}>LABEL OVERRIDE</th>
                  <th style={{ padding: '8px', color: '#6B7280', fontWeight: 600, fontSize: 11, letterSpacing: 0.4 }}>SUBTITLE</th>
                  <th style={{ padding: '8px', width: 70, color: '#6B7280', fontWeight: 600, fontSize: 11, letterSpacing: 0.4 }}>OPT?</th>
                  <th style={{ padding: '8px', width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d, i) => (
                  <RequiredDocRow
                    key={d.id}
                    row={d}
                    index={i + 1}
                    saving={savingId === d.id}
                    onSave={(patch) => updateRow(d.id, patch)}
                    onDelete={() => deleteRow(d.id)}
                  />
                ))}
                <tr style={{ background: '#F9FAFB' }}>
                  <td style={{ padding: '10px 8px', color: '#9CA3AF', fontSize: 12 }}>
                    {docs.length + 1}
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    <select
                      value={newDraft.document_type_id}
                      onChange={(e) =>
                        setNewDraft((p) => ({ ...p, document_type_id: e.target.value }))
                      }
                      style={cellInput}
                    >
                      <option value="">Pick a document type…</option>
                      {availableTypes.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} ({t.code})
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    <input
                      type="text"
                      value={newDraft.custom_label}
                      onChange={(e) =>
                        setNewDraft((p) => ({ ...p, custom_label: e.target.value }))
                      }
                      placeholder="Override catalog name"
                      style={cellInput}
                    />
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    <input
                      type="text"
                      value={newDraft.subtitle}
                      onChange={(e) =>
                        setNewDraft((p) => ({ ...p, subtitle: e.target.value }))
                      }
                      placeholder="e.g. For Mexican notary"
                      style={cellInput}
                    />
                  </td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={newDraft.optional}
                      onChange={(e) =>
                        setNewDraft((p) => ({ ...p, optional: e.target.checked }))
                      }
                    />
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    <button
                      className="btn-action btn-edit"
                      disabled={savingId === 'new'}
                      onClick={addRow}
                      title="Add"
                      style={{ width: 28, height: 24 }}
                    >
                      +
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>

        <div
          style={{
            padding: '12px 24px',
            borderTop: '1px solid #E5E7EB',
            display: 'flex', justifyContent: 'flex-end', gap: 8,
          }}
        >
          <button className="btn-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

const RequiredDocRow = ({ row, index, saving, onSave, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    custom_label: row.custom_label ?? '',
    subtitle: row.subtitle ?? '',
    optional: row.optional === 1,
  });
  useEffect(() => {
    setDraft({
      custom_label: row.custom_label ?? '',
      subtitle: row.subtitle ?? '',
      optional: row.optional === 1,
    });
  }, [row]);
  const save = async () => {
    await onSave({
      custom_label: draft.custom_label.trim() || null,
      subtitle: draft.subtitle.trim() || null,
      optional: draft.optional,
    });
    setEditing(false);
  };
  return (
    <tr style={{ borderBottom: '1px solid #F3F4F6' }}>
      <td style={{ padding: '10px 8px', color: '#9CA3AF', fontSize: 12 }}>
        {index}
      </td>
      <td style={{ padding: '10px 8px' }}>
        <div style={{ fontWeight: 600 }}>{row.document_type_name}</div>
        <div style={{ fontSize: 11, color: '#9CA3AF' }}>{row.document_type_code}</div>
      </td>
      <td style={{ padding: '10px 8px' }}>
        {editing ? (
          <input
            type="text"
            value={draft.custom_label}
            onChange={(e) => setDraft((d) => ({ ...d, custom_label: e.target.value }))}
            style={cellInput}
          />
        ) : (
          <span style={{ color: row.custom_label ? '#111827' : '#9CA3AF' }}>
            {row.custom_label || '—'}
          </span>
        )}
      </td>
      <td style={{ padding: '10px 8px' }}>
        {editing ? (
          <input
            type="text"
            value={draft.subtitle}
            onChange={(e) => setDraft((d) => ({ ...d, subtitle: e.target.value }))}
            style={cellInput}
          />
        ) : (
          <span style={{ color: row.subtitle ? '#111827' : '#9CA3AF' }}>
            {row.subtitle || '—'}
          </span>
        )}
      </td>
      <td style={{ padding: '10px 8px', textAlign: 'center' }}>
        {editing ? (
          <input
            type="checkbox"
            checked={draft.optional}
            onChange={(e) => setDraft((d) => ({ ...d, optional: e.target.checked }))}
          />
        ) : (
          row.optional === 1 ? '✓' : '—'
        )}
      </td>
      <td style={{ padding: '10px 8px', whiteSpace: 'nowrap' }}>
        {editing ? (
          <>
            <button
              className="btn-action btn-edit"
              disabled={saving}
              onClick={save}
              title="Save"
              style={{ width: 24, height: 22 }}
            >
              ✓
            </button>
            <button
              className="btn-action btn-delete"
              disabled={saving}
              onClick={() => setEditing(false)}
              title="Cancel"
              style={{ width: 24, height: 22 }}
            >
              ×
            </button>
          </>
        ) : (
          <>
            <button
              className="btn-action btn-edit"
              disabled={saving}
              onClick={() => setEditing(true)}
              title="Edit"
              style={{ width: 24, height: 22 }}
            >
              ✎
            </button>
            <button
              className="btn-action btn-delete"
              disabled={saving}
              onClick={onDelete}
              title="Delete"
              style={{ width: 24, height: 22 }}
            >
              ×
            </button>
          </>
        )}
      </td>
    </tr>
  );
};

const cellInput = {
  width: '100%',
  padding: '6px 8px',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  fontFamily: 'inherit',
  fontSize: 12.5,
  background: '#FFFFFF',
  boxSizing: 'border-box',
};

const ComponentRow = ({ row, saving, onSave, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    label: row.label,
    amount: (Number(row.amount_cents) / 100).toFixed(2),
    currency: row.currency,
    displayOrder: row.display_order,
  });
  // Reset draft when the underlying row changes (after save refresh).
  useEffect(() => {
    setDraft({
      label: row.label,
      amount: (Number(row.amount_cents) / 100).toFixed(2),
      currency: row.currency,
      displayOrder: row.display_order,
    });
  }, [row]);

  const fmt = (cents, ccy) => {
    const sym = ccy === 'USD' ? '$' : ccy === 'EUR' ? '€' : ccy === 'GBP' ? '£' : ccy === 'INR' ? '₹' : '';
    return sym
      ? `${sym}${(Number(cents) / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}`
      : `${(Number(cents) / 100).toLocaleString('en-US')} ${ccy}`;
  };

  if (!editing) {
    return (
      <tr style={{ borderBottom: '1px solid #F3F4F6' }}>
        <td style={{ padding: '10px 8px' }}>{row.display_order}</td>
        <td style={{ padding: '10px 8px', fontWeight: 500 }}>{row.label}</td>
        <td style={{ padding: '10px 8px', fontVariantNumeric: 'tabular-nums' }}>
          {fmt(row.amount_cents, row.currency)}
        </td>
        <td style={{ padding: '10px 8px' }}>{row.currency}</td>
        <td style={{ padding: '10px 8px' }}>
          <button
            className="naytive-incorp-link"
            disabled={saving}
            onClick={() => setEditing(true)}
            style={{ marginRight: 6 }}
          >
            Edit
          </button>
          <button
            className="naytive-incorp-link"
            disabled={saving}
            onClick={onDelete}
            style={{ color: '#B91C1C' }}
          >
            Delete
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr style={{ borderBottom: '1px solid #F3F4F6', background: '#FFFBEB' }}>
      <td style={{ padding: '10px 8px' }}>
        <input
          type="number"
          value={draft.displayOrder}
          onChange={(e) => setDraft((p) => ({ ...p, displayOrder: e.target.value }))}
          style={cellInput}
        />
      </td>
      <td style={{ padding: '10px 8px' }}>
        <input
          type="text"
          value={draft.label}
          onChange={(e) => setDraft((p) => ({ ...p, label: e.target.value }))}
          style={cellInput}
        />
      </td>
      <td style={{ padding: '10px 8px' }}>
        <input
          type="number"
          step="0.01"
          value={draft.amount}
          onChange={(e) => setDraft((p) => ({ ...p, amount: e.target.value }))}
          style={cellInput}
        />
      </td>
      <td style={{ padding: '10px 8px' }}>
        <select
          value={draft.currency}
          onChange={(e) => setDraft((p) => ({ ...p, currency: e.target.value }))}
          style={cellInput}
        >
          <option>USD</option>
          <option>EUR</option>
          <option>GBP</option>
          <option>INR</option>
          <option>MXN</option>
          <option>AED</option>
          <option>SGD</option>
        </select>
      </td>
      <td style={{ padding: '10px 8px' }}>
        <button
          className="naytive-incorp-btn"
          disabled={saving}
          onClick={async () => {
            await onSave({
              label: draft.label.trim(),
              amountCents: Math.round(Number(draft.amount) * 100),
              currency: draft.currency,
              displayOrder: Number(draft.displayOrder),
            });
            setEditing(false);
          }}
        >
          {saving ? '…' : 'Save'}
        </button>
        <button
          className="naytive-incorp-link"
          disabled={saving}
          onClick={() => setEditing(false)}
          style={{ marginLeft: 6 }}
        >
          Cancel
        </button>
      </td>
    </tr>
  );
};

const JurisdictionsTab = ({ flash }) => {
  // Pricing modal — opened per-row from the "Pricing" link in the
  // actions cell. Holds the entire jurisdiction row so the modal can
  // render the country / slug header without a refetch.
  const [pricingFor, setPricingFor] = useState(null);
  const [docsFor, setDocsFor] = useState(null);
  return (
    <JurisdictionsTabInner
      flash={flash}
      pricingFor={pricingFor} setPricingFor={setPricingFor}
      docsFor={docsFor} setDocsFor={setDocsFor}
    />
  );
};

const JurisdictionsTabInner = ({
  flash,
  pricingFor, setPricingFor,
  docsFor, setDocsFor,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await naytiveAPI.listIncorpJurisdictions();
      setRows(r.data.items || []);
    } catch (err) {
      console.error(err);
      flash('Failed to load jurisdictions', 'error');
    } finally {
      setLoading(false);
    }
  }, [flash]);

  useEffect(() => { load(); }, [load]);

  const saveOne = async (id, patch) => {
    try {
      await naytiveAPI.updateIncorpJurisdiction(id, patch);
      flash('Saved · engine cache reloaded', 'success');
      load();
    } catch (err) {
      console.error(err);
      flash('Save failed', 'error');
    }
  };

  // Edit modal target (whole row) — we open the JurisdictionEditor
  // in a centred modal instead of an inline expanding row so the
  // ~30+ editable fields don't blow the table layout.
  const editRow = rows.find((r) => r.id === openId) || null;
  return (
    <>
      <div className="dashboard-section">
        {loading ? (
          <div className="loading-state">
            <div className="spinner" />
            <p>Loading 15 jurisdictions…</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="empty-state">
            <p>No jurisdictions configured.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>SLUG</th>
                  <th>NAME</th>
                  <th>COUNTRY</th>
                  <th>ENTITY (DEFAULT / VC)</th>
                  <th>WEEKS</th>
                  <th>COST (Y1 RANGE)</th>
                  <th>FLAGS</th>
                  <th>ACTIVE</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <code className="naytive-incorp-mono">{r.slug}</code>
                    </td>
                    <td>
                      <span style={{ fontWeight: 500 }}>{r.name}</span>
                    </td>
                    <td>{r.country_code}</td>
                    <td>
                      {r.entity_default}
                      {r.entity_for_vc !== r.entity_default
                        ? ` · ${r.entity_for_vc}`
                        : ''}
                    </td>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {Number(r.setup_weeks).toFixed(1)}
                    </td>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                      ${Math.round(r.cost_min)}–${Math.round(r.cost_max)}
                    </td>
                    <td>
                      {r.vc_standard ? (
                        <span className="chip vc">VC</span>
                      ) : null}{' '}
                      {r.has_us_treaty ? (
                        <span className="chip ust">US-treaty</span>
                      ) : null}{' '}
                      {r.naytive_corridor ? (
                        <span className="chip cor">Naytive</span>
                      ) : null}{' '}
                      {r.banking_penalty !== 0 ? (
                        <span className="chip warn">
                          Banking {r.banking_penalty}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => saveOne(r.id, { is_active: !r.is_active })}
                        title={r.is_active
                          ? 'Live for founders — click to turn OFF'
                          : 'Hidden from founders — click to turn ON'}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '4px 11px', borderRadius: 999, cursor: 'pointer',
                          border: `1px solid ${r.is_active ? '#A7F3D0' : '#E5E7EB'}`,
                          background: r.is_active ? '#ECFDF5' : '#F9FAFB',
                          color: r.is_active ? '#047857' : '#6B7280',
                          fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                        }}
                      >
                        <span style={{
                          width: 8, height: 8, borderRadius: 999,
                          background: r.is_active ? '#10B981' : '#9CA3AF',
                        }} />
                        {r.is_active ? 'On' : 'Off'}
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn-action btn-edit"
                        onClick={() => setPricingFor(r)}
                        title="Manage tentative pricing"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="12" y1="1" x2="12" y2="23"></line>
                          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                        </svg>
                      </button>
                      <button
                        className="btn-action btn-edit"
                        onClick={() => setDocsFor(r)}
                        title="Required documents"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="9" y1="13" x2="15" y2="13"></line>
                          <line x1="9" y1="17" x2="15" y2="17"></line>
                        </svg>
                      </button>
                      <button
                        className="btn-action btn-edit"
                        onClick={() => setOpenId(r.id)}
                        title="Edit jurisdiction"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 20h9"></path>
                          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {pricingFor && (
        <PricingModal
          row={pricingFor}
          onClose={() => setPricingFor(null)}
          flash={flash}
        />
      )}
      {docsFor && (
        <RequiredDocsModal
          row={docsFor}
          onClose={() => setDocsFor(null)}
          flash={flash}
        />
      )}
      {editRow && (
        <JurisdictionEditorModal
          row={editRow}
          onClose={() => setOpenId(null)}
          onSave={async (patch) => {
            await saveOne(editRow.id, patch);
            setOpenId(null);
          }}
        />
      )}
    </>
  );
};

const JurisdictionEditorModal = ({ row, onSave, onClose }) => {
  // Editable hot fields — cost / weeks / industry / tax / banking. Anything
  // more involved (insights, market_match_regions) is left raw-JSON until
  // there's a real ops need for a richer form.
  const [draft, setDraft] = useState({
    tagline: row.tagline ?? '',
    blurb: row.blurb ?? '',
    setup_weeks: row.setup_weeks,
    cost_min: row.cost_min,
    cost_max: row.cost_max,
    recur_min: row.recur_min,
    recur_max: row.recur_max,
    tax_personal_score: row.tax_personal_score,
    tax_corp_score: row.tax_corp_score,
    banking_penalty: row.banking_penalty,
  });
  const setF = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const industryKeys = [
    'crypto_web3',
    'fintech',
    'saas_software',
    'ecommerce',
    'services_consulting',
    'ip_patents',
  ];
  const industryInit = useMemo(() => {
    try {
      return typeof row.industry_scores === 'string'
        ? JSON.parse(row.industry_scores)
        : row.industry_scores || {};
    } catch {
      return {};
    }
  }, [row.industry_scores]);
  const [industry, setIndustry] = useState(industryInit);

  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        ...draft,
        setup_weeks: Number(draft.setup_weeks),
        cost_min: Number(draft.cost_min),
        cost_max: Number(draft.cost_max),
        recur_min: Number(draft.recur_min),
        recur_max: Number(draft.recur_max),
        tax_personal_score: Number(draft.tax_personal_score),
        tax_corp_score: Number(draft.tax_corp_score),
        banking_penalty: Number(draft.banking_penalty),
        industry_scores: industry,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content modal-form"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
      >
        <div className="modal-header">
          <h2 className="modal-title">
            Edit {row.name}{' '}
            <span style={{ color: '#9CA3AF', fontSize: 13, fontWeight: 400 }}>
              ({row.slug} · {row.country_code})
            </span>
          </h2>
        </div>
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div className="modal-body" style={{ overflowY: 'auto' }}>
            <div className="form-group full-width">
              <label>Tagline</label>
              <input
                type="text"
                value={draft.tagline}
                onChange={(e) => setF('tagline', e.target.value)}
                placeholder="One-line marketing pitch"
              />
            </div>
            <div className="form-group full-width">
              <label>Blurb</label>
              <textarea
                rows={2}
                value={draft.blurb}
                onChange={(e) => setF('blurb', e.target.value)}
                placeholder="2-line description"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Setup Weeks</label>
                <input
                  type="number"
                  step="0.5"
                  value={draft.setup_weeks}
                  onChange={(e) => setF('setup_weeks', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Banking Penalty</label>
                <input
                  type="number"
                  value={draft.banking_penalty}
                  onChange={(e) => setF('banking_penalty', e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Cost Min ($)</label>
                <input
                  type="number"
                  value={draft.cost_min}
                  onChange={(e) => setF('cost_min', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Cost Max ($)</label>
                <input
                  type="number"
                  value={draft.cost_max}
                  onChange={(e) => setF('cost_max', e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Recurring Min ($)</label>
                <input
                  type="number"
                  value={draft.recur_min}
                  onChange={(e) => setF('recur_min', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Recurring Max ($)</label>
                <input
                  type="number"
                  value={draft.recur_max}
                  onChange={(e) => setF('recur_max', e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Tax Personal Score</label>
                <input
                  type="number"
                  value={draft.tax_personal_score}
                  onChange={(e) => setF('tax_personal_score', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Tax Corp Score</label>
                <input
                  type="number"
                  value={draft.tax_corp_score}
                  onChange={(e) => setF('tax_corp_score', e.target.value)}
                />
              </div>
            </div>

            <div
              style={{
                marginTop: 18,
                marginBottom: 8,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.5,
                color: '#9CA3AF',
                textTransform: 'uppercase',
              }}
            >
              Industry Scores
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 12,
              }}
            >
              {industryKeys.map((k) => (
                <div className="form-group" key={k}>
                  <label>{k.replace(/_/g, ' ')}</label>
                  <input
                    type="number"
                    value={industry[k] ?? 0}
                    onChange={(e) =>
                      setIndustry((s) => ({ ...s, [k]: Number(e.target.value) }))
                    }
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SCORING CONFIG TAB
// ─────────────────────────────────────────────────────────────────────────────

const ConfigTab = ({ flash }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await naytiveAPI.listScoringConfig();
      setRows(r.data.items || []);
      // Pre-fill draft strings so the user is editing the JSON text directly.
      const ds = {};
      for (const row of (r.data.items || [])) {
        const v = typeof row.config_value === 'string'
          ? row.config_value
          : JSON.stringify(row.config_value);
        ds[row.config_key] = v;
      }
      setDrafts(ds);
    } catch (err) {
      console.error(err);
      flash('Failed to load config', 'error');
    } finally {
      setLoading(false);
    }
  }, [flash]);

  useEffect(() => { load(); }, [load]);

  const saveOne = async (key) => {
    try {
      const raw = drafts[key];
      const parsed = JSON.parse(raw);
      await naytiveAPI.updateScoringConfig(key, parsed);
      flash(`${key} updated · engine reloaded`, 'success');
      load();
    } catch (err) {
      flash(`Invalid JSON for ${key}`, 'error');
    }
  };

  return (
    <div className="dashboard-section">
      {loading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading config…</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>KEY</th>
                <th>VALUE (JSON)</th>
                <th>DESCRIPTION</th>
                <th style={{ width: 90 }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.config_key}>
                  <td>
                    <code className="naytive-incorp-mono">{r.config_key}</code>
                  </td>
                  <td>
                    <input
                      className="naytive-incorp-mono"
                      style={{
                        width: '100%',
                        padding: '6px 10px',
                        border: '1px solid #D1D5DB',
                        borderRadius: 6,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        fontSize: 12,
                        background: '#FFFFFF',
                        boxSizing: 'border-box',
                      }}
                      value={drafts[r.config_key] ?? ''}
                      onChange={(e) =>
                        setDrafts((d) => ({ ...d, [r.config_key]: e.target.value }))
                      }
                    />
                  </td>
                  <td style={{ fontSize: 12, color: '#6B7280', maxWidth: 320 }}>
                    {r.description}
                  </td>
                  <td>
                    <button
                      className="btn-save"
                      style={{ padding: '6px 14px', fontSize: 12 }}
                      onClick={() => saveOne(r.config_key)}
                    >
                      Save
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// RECOMMENDATIONS LOG TAB
// ─────────────────────────────────────────────────────────────────────────────

const LogTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ stage: '', primary: '', override: '' });
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const f = {};
      if (filters.stage) f.stage = filters.stage;
      if (filters.primary) f.primary = filters.primary;
      if (filters.override) f.override = '1';
      const r = await naytiveAPI.listRecommendations(f);
      setRows(r.data.items || []);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="dashboard-section">
      <div className="na-toolbar">
        <div className="na-toolbar-filters">
          <select
            className="na-status-select"
            value={filters.stage}
            onChange={(e) => setFilters((f) => ({ ...f, stage: e.target.value }))}
          >
            <option value="">All stages</option>
            <option value="stage1">Stage 1</option>
            <option value="stage2">Stage 2 (refine)</option>
          </select>
          <input
            className="na-status-select"
            placeholder="Primary slug (e.g. delaware)"
            value={filters.primary}
            onChange={(e) =>
              setFilters((f) => ({ ...f, primary: e.target.value }))
            }
            style={{ minWidth: 220 }}
          />
          <label
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}
          >
            <input
              type="checkbox"
              checked={filters.override === '1'}
              onChange={(e) =>
                setFilters((f) => ({ ...f, override: e.target.checked ? '1' : '' }))
              }
            />
            Overrides only
          </label>
        </div>
        <span className="na-toolbar-count">{rows.length} showing</span>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading log…</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <p>No recommendations match the filters.</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>WHEN</th>
                <th>STAGE</th>
                <th>PRIMARY</th>
                <th>MATCH%</th>
                <th>OVERRIDE FROM</th>
                <th>PICKED</th>
                <th>SESSION</th>
                <th style={{ width: 90 }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr>
                    <td style={{ fontSize: 12, color: '#6B7280' }}>
                      {new Date(r.created_at).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td>{r.stage}</td>
                    <td>
                      <code className="naytive-incorp-mono">{r.primary_slug}</code>
                    </td>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {r.primary_match_pct}%
                    </td>
                    <td>
                      {r.override_from_slug ? (
                        <code className="naytive-incorp-mono">
                          {r.override_from_slug}
                        </code>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      {r.picked_slug ? (
                        <code className="naytive-incorp-mono">{r.picked_slug}</code>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <code className="naytive-incorp-mono">
                        {(r.session_id || '').slice(0, 16)}
                      </code>
                    </td>
                    <td>
                      <button
                        className="btn-action btn-edit"
                        onClick={() =>
                          setOpenId(openId === r.id ? null : r.id)
                        }
                        title={openId === r.id ? 'Close' : 'Inspect'}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      </button>
                    </td>
                  </tr>
                  {openId === r.id && (
                    <tr>
                      <td colSpan={8} style={{ background: '#F9FAFB' }}>
                        <pre
                          style={{
                            margin: 0,
                            padding: '12px 16px',
                            fontFamily:
                              'ui-monospace, SFMono-Regular, Menlo, monospace',
                            fontSize: 11.5,
                            color: '#1F2937',
                            background: '#F9FAFB',
                            overflowX: 'auto',
                          }}
                        >
                          {JSON.stringify(
                            {
                              answers: r.answers,
                              secondary_slugs: r.secondary_slugs,
                            },
                            null,
                            2,
                          )}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NaytiveIncorporation;
